/**
 * Список организаций входа клиента-компании с поиском (multi-organization-login): общий для окна
 * выбора после входа и переключателя в шапке. У сетевиков организаций десятки, поэтому поиск —
 * по названию и ИНН, недавно выбранные идут первыми (порядок задаёт сервер).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Building2, ChevronRight } from 'lucide-react'
import { authApi, type Organization } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Badge } from '@/design-system/components/primitives/badge'
import { Input } from '@/design-system/components/primitives/input'
import { cn } from '@/lib/utils'

/** Показывать поле поиска, когда организаций больше этого числа. */
const SEARCH_FROM = 6

/** Совпадение организации с поисковой строкой: по названию (полному и краткому) и ИНН. */
export function organizationMatches(org: Organization, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [org.name, org.nameShort, org.inn].some((v) => v.toLowerCase().includes(q))
}

/**
 * Название организации в списке: краткое наименование (ООО «…»), если оно задано, иначе полное.
 * Полные наименования вида «ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ …» начинаются одинаково, поэтому
 * название не обрезается, а переносится по словам целиком (заказчица 21.10.2026: «названия не
 * видно, только по ИНН можно ориентироваться»).
 */
export function organizationTitle(org: Organization): string {
  return org.nameShort.trim() || org.name
}

export interface OrganizationPickerProps {
  /** Выбор организации; промис — пока выполняется, список блокируется. */
  onSelect: (clientId: string) => Promise<void>
  /** Единственную организацию без активной выбрать сразу (окно выбора после входа). */
  autoSelectSingle?: boolean
}

export function OrganizationPicker({ onSelect, autoSelectSingle = false }: OrganizationPickerProps) {
  const [items, setItems] = useState<Organization[] | null>(null)
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function choose(clientId: string) {
    setError(null)
    setBusyId(clientId)
    try {
      await onSelect(clientId)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось открыть организацию'))
      setBusyId(null)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    authApi
      .organizations(controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return
        setItems(res.items)
        if (autoSelectSingle && res.items.length === 1 && !res.items[0].active) {
          void choose(res.items[0].id)
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(getErrorMessage(err, 'Не удалось загрузить список организаций'))
          setItems([])
        }
      })
    return () => controller.abort()
    // choose зависит только от onSelect; список грузится один раз при показе.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visible = useMemo(() => (items ?? []).filter((o) => organizationMatches(o, query)), [items, query])

  if (items === null) {
    return <p className="text-txt-secondary">Загрузка организаций…</p>
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    // Enter в поиске открывает организацию, если она одна в выдаче.
    if (visible.length === 1 && busyId === null) void choose(visible[0].id)
  }

  return (
    <div>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {items.length > SEARCH_FROM ? (
        <form onSubmit={onSubmit} className="sticky top-0 z-10 mb-3 bg-white pb-1" role="search">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по названию или ИНН"
            aria-label="Поиск организации"
          />
        </form>
      ) : null}
      {items.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Нет доступных организаций"
          description="За вашим входом не закреплено ни одной организации. Обратитесь к менеджеру Центрики."
          className="py-8"
        />
      ) : visible.length === 0 ? (
        <p className="py-4 text-center text-txt-secondary">Ничего не найдено</p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-12 border border-line">
          {visible.map((org) => (
            <li key={org.id}>
              <button
                type="button"
                onClick={() => void choose(org.id)}
                disabled={busyId !== null}
                aria-current={org.active ? 'true' : undefined}
                className={cn(
                  'group flex w-full items-center justify-between gap-3 px-4 py-3 text-left outline-none transition-colors',
                  'hover:bg-accent-bg focus-visible:bg-accent-bg focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  org.active ? 'bg-subtle' : null,
                )}
              >
                <span className="min-w-0">
                  <span className="block break-words font-medium text-txt" title={org.name}>
                    {organizationTitle(org)}
                  </span>
                  {org.inn ? <span className="block text-12 text-txt-tertiary">ИНН {org.inn}</span> : null}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {org.unread > 0 ? (
                    <Badge variant="blue" title="Непрочитанные уведомления">
                      {org.unread}
                    </Badge>
                  ) : null}
                  {org.active ? <Badge variant="green">Текущая</Badge> : null}
                  {busyId === org.id ? <span className="text-12 text-txt-secondary">Открываем…</span> : null}
                  <ChevronRight
                    className="size-4 stroke-current text-txt-tertiary transition-colors group-hover:text-accent group-focus-visible:text-accent"
                    aria-hidden
                  />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
